import { NavLink } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { useSectionsWithChannels } from '@/hooks/useSections';
import {
  Hash,
  Lock,
  Loader2,
  Megaphone,
  MessageSquare,
  Briefcase,
  UserPlus,
  Handshake,
  Users,
  Sparkles,
  CircleHelp,
  LucideIcon,
} from 'lucide-react';
import type { Database } from '@/integrations/supabase/types';

type ChannelType = Database['public']['Enums']['channel_type'];

const CHANNEL_TYPE_CONFIG: Record<ChannelType, { icon: typeof Hash; color: string }> = {
  discussion: { icon: MessageSquare, color: 'text-blue-500' },
  announcements: { icon: Megaphone, color: 'text-orange-500' },
  jobs: { icon: Briefcase, color: 'text-green-500' },
  intros: { icon: UserPlus, color: 'text-purple-500' },
};

const SECTION_ICON_MAP: Record<string, LucideIcon> = {
  function: Briefcase,
  functions: Briefcase,
  contributing: Handshake,
  contribution: Handshake,
  interest: Sparkles,
  interests: Sparkles,
  cohort: Users,
  cohorts: Users,
  everyone: Users,
  other: CircleHelp,
};

function getSectionIcon(sectionName: string): LucideIcon {
  const key = sectionName.trim().toLowerCase();
  return SECTION_ICON_MAP[key] || Hash;
}

export default function ChannelsPage() {
  const { data: sections, isLoading } = useSectionsWithChannels();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!sections || sections.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Channels</h1>
          <p className="text-muted-foreground text-sm">
            No channels available
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Channels</h1>
        <p className="text-muted-foreground text-sm">
          Join conversations in different topics
        </p>
      </div>

      {sections.map((section) => {
        const SectionIcon = getSectionIcon(section.name);
        return (
          <div key={section.id} className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <SectionIcon className="h-4 w-4" />
              {section.name}
              <span className="text-muted-foreground/60 font-normal normal-case">
                ({section.channels.length})
              </span>
            </h2>

            {section.channels.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No channels in this section
              </p>
            ) : (
              <div className="grid gap-3">
                {section.channels.map((channel) => {
                  const typeConfig = CHANNEL_TYPE_CONFIG[channel.channel_type];
                  const TypeIcon = typeConfig?.icon || Hash;

                  return (
                    <NavLink key={channel.id} to={`/channels/${channel.slug}`}>
                      <Card className="card-qbn hover:shadow-qbn-float transition-shadow cursor-pointer">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`mt-0.5 ${typeConfig?.color || 'text-muted-foreground'}`}>
                              <TypeIcon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-foreground">
                                  #{channel.slug}
                                </span>
                                {channel.is_restricted && (
                                  <Lock className="h-3 w-3 text-muted-foreground" />
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {channel.description || channel.name}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
